import { supabase } from '@/integrations/supabase/client';
import { AppState } from '@/types';
import { transformAppStateFromSupabase } from '@/utils/supabaseDataTransforms';
import { loadHabitsFromSupabase, loadDailiesFromSupabase, loadTodosFromSupabase } from '@/utils/supabaseHabitLoader';

export async function loadUserDataFromSupabase(userId: string): Promise<AppState | null> {
  try {
    // Load all data in parallel for maximum performance
    const [
      profileResult,
      statsResult,
      activitiesResult,
      simplePetResult,
      petResult,
      challengesResult,
      habitsResult,
      dailiesResult,
      todosResult
    ] = await Promise.all([
      // Profile
      supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle(),
      
      // User stats - only needed columns
      supabase
        .from('user_stats')
        .select('user_id, total_points, current_week_points, current_month_points, streak, experience, level')
        .eq('user_id', userId)
        .maybeSingle(),
      
      // Activities - LIMITED to last 100 for initial load
      supabase
        .from('activities')
        .select(`
          *,
          activity_comments (
            *
          )
        `)
        .eq('user_id', userId)
        .order('timestamp', { ascending: false })
        .limit(100),
      
      // Simple pet
      supabase
        .from('simple_pets')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle(),
      
      // Virtual pet
      supabase
        .from('virtual_pets')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle(),
      
      // Challenges - with limit
      supabase
        .from('challenges')
        .select('*')
        .or(`user_id.eq.${userId},is_public.eq.true`)
        .order('created_at', { ascending: false })
        .limit(20),
      
      // Habits, dailies, todos in parallel within the main parallel call
      loadHabitsFromSupabase(userId),
      loadDailiesFromSupabase(userId),
      loadTodosFromSupabase(userId)
    ]);

    // Extract data and handle errors
    const profile = profileResult.data;
    const stats = statsResult.data;
    const activities = activitiesResult.data;
    const simplePet = simplePetResult.data;
    const pet = petResult.data;
    const challenges = challengesResult.data || [];

    // Log errors in development only
    if (import.meta.env.DEV) {
      if (profileResult.error) console.error('Error loading profile:', profileResult.error);
      if (statsResult.error) console.error('Error loading stats:', statsResult.error);
      if (activitiesResult.error) console.error('Error loading activities:', activitiesResult.error);
      if (simplePetResult.error) console.error('Error loading simple pet:', simplePetResult.error);
      if (petResult.error) console.error('Error loading pet:', petResult.error);
      if (challengesResult.error) console.error('Error loading challenges:', challengesResult.error);
    }

    // Get user profile for name
    const userProfile = profile || await supabase
      .from('profiles')
      .select('name, email')
      .eq('user_id', userId)
      .single()
      .then(({ data }) => data);

    if (import.meta.env.DEV) {
      console.log('📊 Loaded:', activities?.length || 0, 'activities (limited to 100)');
    }

    const allHabits = [...habitsResult, ...dailiesResult, ...todosResult];

    return transformAppStateFromSupabase(
      profile,
      stats,
      activities || [],
      pet,
      challenges,
      userProfile,
      allHabits,
      simplePet
    );

  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Error loading user data:', error);
    }
    return null;
  }
}
